import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLayout } from '../contexts/layout/LayoutContext';
import useStorage from './useStorage';
import Modal from '../ui/modal/Modal';
import useLabels from './useLabels';
import useDebounce from './useDebounce';
import MainUserInstructions, { type MainUserInstructionsProps } from '../elements/chat-mainUserInstructions/MainUserInstructions';
import MainUserInstructionsModalFooter from '../elements/chat-mainuserInstructions-modal-footer/MainUserInstructionsModalFooter';
import MarkdownWrapper from '../ui/markdown-wrapper/MarkdownWrapper';

const storeScope = 'chat-window';
const userInstructionStoreKey = 'is-disclaimer-read';

export default function useUserInstruction() {
    const labels = useLabels();
    const l = useMemo(() => ({ hl: labels.userInstructions, cta: labels.userInstructionButton }), [labels.userInstructionButton, labels.userInstructions]);
    const { isShowingTermsAndConditions, setIsShowingTermsAndConditions } = useLayout();
    const [isUserInstructionRead, setIsDisclaimerRead] = useState<boolean>(false);
    console.log('isDisclaimerRead:', isUserInstructionRead);
    const { get, set } = useStorage(storeScope, 'session');

    useEffect(() => {
        const isRead = get(userInstructionStoreKey);
        setIsShowingTermsAndConditions(!isRead);
    }, [get, setIsShowingTermsAndConditions]);

    const hideDisclaimer = useCallback(() => {
        set(userInstructionStoreKey, true);
        setIsShowingTermsAndConditions(false);
    }, [set, setIsShowingTermsAndConditions]);

    // Debounce to allow modal to animate out
    const debouncedShowDisclaimer = useDebounce(isShowingTermsAndConditions, 300, isShowingTermsAndConditions);
    const renderDisclaimer = useCallback((props: Omit<MainUserInstructionsProps, 'hideButton' | 'onCtaClick' | 'onDisclaimerIsRead'> = {}) => {
        if (!debouncedShowDisclaimer) return null;
        const disclaimer = isShowingTermsAndConditions && <MainUserInstructions {...props} onDisclaimerIsRead={setIsDisclaimerRead} />;
        return <Modal
            size='large'
            headline={l.hl}
            hideCloseIcon
            isOpen={isShowingTermsAndConditions}
            body={disclaimer}
            footer={<MainUserInstructionsModalFooter extra={<MarkdownWrapper markdown={labels.disclaimerConfirmationMarkdown} />}
            button={{ label: l.cta, onClick: hideDisclaimer, isDisabled: !isUserInstructionRead }} />}
        />;
    }, [debouncedShowDisclaimer, isShowingTermsAndConditions, l.hl, l.cta, labels.disclaimerConfirmationMarkdown, hideDisclaimer, isUserInstructionRead]);

    return {
        showDisclaimer: isShowingTermsAndConditions,
        isDisclaimerRead: isUserInstructionRead,
        renderDisclaimer,
    };
}
